import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Button, Chip, CircularProgress } from '@material-ui/core';
import { faUpload, faTrash, faUndo, faInfoCircle, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalWrapper } from 'components/ModalWrapper';
import FileUpload from 'CMFW/input/FileUpload';
import { IAudioDetails } from 'types/audio';
import axios from 'axios';
import {
  deleteAudioReference,
  recoverAudio,
  uploadAudio,
  canManageAudio,
  getAudioSetRestrictionMessage,
} from 'services/audioManagement';

type IProps = {
  audio: IAudioDetails;
  onClose: () => void;
  onAudioChange?: (audio: IAudioDetails) => void;
};

const AudioManagementModal: React.FC<IProps> = ({ audio, onClose, onAudioChange }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  const hasAudio = !!audio.path;
  const canRecover = audio.canRecover;
  const isInActiveSet = !canManageAudio(audio);
  const activeSet = audio.audioSet || null;

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(''), 10000); // Keep error visible for 10 seconds
  };

  const showSuccess = (message: string) => {
    // In a real implementation, you might want to use a proper notification system
    console.log('Success:', message);
  };

  const handleDelete = useCallback(async () => {
    if (isInActiveSet) {
      showError(getAudioSetRestrictionMessage(audio));
      return;
    }

    try {
      setLoading(true);
      const response = await deleteAudioReference(audio.ref, audio.refId, audio.dialectId);

      showSuccess('Audio deleted successfully');

      if (onAudioChange && response?.audio) {
        onAudioChange(response.audio);
      }

      onClose();
    } catch (error) {
      console.error('Delete audio error:', error);
      const errorMessage = (error as any)?.message || 'Failed to delete audio';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [audio, isInActiveSet, onAudioChange, onClose]);

  const handleRecover = useCallback(async () => {
    if (isInActiveSet) {
      showError(getAudioSetRestrictionMessage(audio));
      return;
    }

    try {
      setLoading(true);
      const response = await recoverAudio(audio.ref, audio.refId, audio.dialectId);

      showSuccess('Audio recovered successfully');

      if (onAudioChange && response?.audio) {
        onAudioChange(response.audio);
      }

      onClose();
    } catch (error) {
      console.error('Recover audio error:', error);
      const errorMessage = (error as any)?.message || 'Failed to recover audio';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [audio, isInActiveSet, onAudioChange, onClose]);

  const handleFileSelect = (file: File) => {
    const isAudio = file.type === 'audio/wav' || file.type === 'audio/mp3' || file.type === 'audio/mpeg';
    if (!isAudio) {
      showError('You can only upload WAV or MP3 files!');
      return;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      showError('Audio file must be smaller than 10MB!');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      showError('Please select an audio file');
      return;
    }

    if (isInActiveSet) {
      showError(getAudioSetRestrictionMessage(audio));
      return;
    }

    try {
      setLoading(true);
      const response = await uploadAudio(audio.ref, audio.refId, audio.dialectId, selectedFile);

      showSuccess('Audio uploaded successfully');

      if (onAudioChange && response?.audio) {
        onAudioChange(response.audio);
      }

      onClose();
    } catch (error) {
      console.error('Upload audio error:', error);
      const errorMessage = (error as any)?.message || 'Failed to upload audio';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [audio, selectedFile, isInActiveSet, onAudioChange, onClose]);

  const handleDownload = useCallback(async () => {
    if (!audio.path) {
      showError('No audio file to download');
      return;
    }

    try {
      setLoading(true);

      // Use the dedicated download endpoint
      const downloadURL = `/audio/${audio.ref}/${audio.refId}/dialect/${audio.dialectId}/download`;

      // Make axios request to get file data (following DownloadLink pattern)
      const response = await axios.get<{ result: string; fileData: string; fileName: string; mimeType: string }>(
        downloadURL,
      );

      if (response.data.result !== 'OK') {
        showError('Failed to download audio file');
        return;
      }

      // Convert base64 to blob
      const binaryString = atob(response.data.fileData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: response.data.mimeType });

      // Create download link
      const linkUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = linkUrl;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(linkUrl);

      showSuccess('Download completed');
    } catch (error) {
      console.error('Download audio error:', error);
      const errorMessage = (error as any)?.response?.data?.error || 'Failed to download audio';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [audio]);

  return (
    <ModalWrapper title="Audio Management" onClose={onClose} height={400} width={500}>
      <Container>
        {loading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}

        {isInActiveSet && (
          <WarningSection>
            <FontAwesomeIcon icon={faInfoCircle} />
            <WarningText>
              <WarningTitle>Audio Set Warning</WarningTitle>
              <WarningDescription>{getAudioSetRestrictionMessage(audio)}</WarningDescription>
            </WarningText>
          </WarningSection>
        )}

        {error && <ErrorSection>{error}</ErrorSection>}

        <InfoSection>
          <InfoRow>
            <InfoLabel>Reference:</InfoLabel>
            <InfoValue>{audio.ref}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Reference ID:</InfoLabel>
            <InfoValue>{audio.refId}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Dialect ID:</InfoLabel>
            <InfoValue>{audio.dialectId}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Status:</InfoLabel>
            <InfoValue>
              <StatusChip
                label={hasAudio ? 'Has audio' : canRecover ? 'Can be recovered' : 'No audio'}
                color={hasAudio ? 'primary' : canRecover ? 'secondary' : 'default'}
                size="small"
              />
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Audio Set:</InfoLabel>
            <InfoValue>{activeSet ? activeSet.name : '—'}</InfoValue>
          </InfoRow>
          {activeSet && (
            <InfoRow>
              <InfoLabel>Audio Set ID:</InfoLabel>
              <InfoValue>
                <SelectableText>{String(activeSet.id)}</SelectableText>
              </InfoValue>
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>Artist:</InfoLabel>
            <InfoValue>{audio.artistName || '—'}</InfoValue>
          </InfoRow>
        </InfoSection>

        <ActionsSection>
          {hasAudio && (
            <>
              <ActionButton
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faDownload} />}
                onClick={handleDownload}
                disabled={loading}
                fullWidth
              >
                Download Audio
              </ActionButton>
              <ActionButton
                variant="contained"
                color="secondary"
                startIcon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleDelete}
                disabled={isInActiveSet || loading}
                fullWidth
              >
                Delete Audio Reference
              </ActionButton>
            </>
          )}

          {!hasAudio && canRecover && (
            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<FontAwesomeIcon icon={faUndo} />}
              onClick={handleRecover}
              disabled={isInActiveSet || loading}
              fullWidth
            >
              Recover Audio
            </ActionButton>
          )}

          {!hasAudio && !canRecover && (
            <>
              <FileUploadContainer>
                <FileUpload handleFile={handleFileSelect} />
                {selectedFile && (
                  <SelectedFileInfo>
                    Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)}KB)
                  </SelectedFileInfo>
                )}
              </FileUploadContainer>

              {selectedFile && (
                <ActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<FontAwesomeIcon icon={faUpload} />}
                  onClick={handleUpload}
                  disabled={isInActiveSet || loading}
                  fullWidth
                >
                  Upload Audio
                </ActionButton>
              )}
            </>
          )}
        </ActionsSection>
      </Container>
    </ModalWrapper>
  );
};

export default AudioManagementModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const WarningSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  color: #856404;

  svg {
    margin-top: 2px;
    color: #f39c12;
  }
`;

const WarningText = styled.div`
  flex: 1;
`;

const WarningTitle = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`;

const WarningDescription = styled.div`
  font-size: 14px;
`;

const ErrorSection = styled.div`
  padding: 12px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
`;

const InfoSection = styled.div`
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #666;
`;

const InfoValue = styled.span`
  color: #333;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const StatusChip = styled(Chip)`
  && {
    font-size: 12px;
  }
`;

const SelectableText = styled.span`
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
`;

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  && {
    height: 48px;
    text-transform: none;
  }
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SelectedFileInfo = styled.div`
  font-size: 14px;
  color: #666;
  padding: 8px;
  background: #f0f0f0;
  border-radius: 4px;
`;
